import { Avatar, AvatarFallback, AvatarImage } from "@spz/ui/components/avatar";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
} from "@spz/ui/components/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@spz/ui/components/select";
import { TextField } from "@spz/ui/components/textfield";
import { Toggle } from "@spz/ui/components/toggle";
import { Typography } from "@spz/ui/components/typography";
import { cn } from "@spz/ui/lib/utils";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";
import { useUserProfileViewModel } from "../../user-profile/use-user-profile.viewmodel";
import { PasswordResetButton } from "../components/password-reset";

interface UserProfileFormProps {
  className?: string;
  onSuccess?: () => void;
  ref?: Ref<HTMLFormElement>;
}
export const UserProfileForm = ({
  className,
  onSuccess,
  ref,
}: UserProfileFormProps) => {
  const { t } = useTranslation();
  const {
    form,
    onSubmit,
    handleAvatarUpload,
    handleAvatarValidationError,
    preview,
    isUsernamePassword,
    me,
    isNotificationsEnabledLoading,
    handleNotificationsPreferencesChanges,
    notificationsEnabled,
    appLanguageOptions,
    handleAutosave,
  } = useUserProfileViewModel();

  return (
    <Form {...form}>
      <form
        ref={ref}
        onSubmit={onSubmit}
        id="user-profile-form" // Required for the submit button
        className={cn("space-y-4 px-6 pb-6", className)}
        data-testid="profile-popup"
      >
        {/* Avatar and Notifications Section */}
        <div className="flex w-full justify-between">
          <FormField
            control={form.control}
            name="avatarUrl"
            render={({ field }) => (
              <Avatar
                showEditButton
                onEdit={handleAvatarUpload}
                onValidationError={handleAvatarValidationError}
              >
                {preview && <AvatarImage src={preview} />}
                <AvatarFallback
                  firstName={form.watch("firstName")}
                  lastName={form.watch("lastName")}
                />
              </Avatar>
            )}
          />
          <div className="flex flex-col gap-4 items-end justify-end">
            {isUsernamePassword && (
              <PasswordResetButton
                className="p-0 h-auto"
                variant="link"
                data-testid="profile-change-password-link"
              />
            )}
            <div className="flex space-x-4 items-center justify-between">
              <Typography className="text-gray-900 font-title">
                {t("email_notifications")}
              </Typography>
              <Toggle
                checked={notificationsEnabled}
                disabled={isNotificationsEnabledLoading}
                onCheckedChange={handleNotificationsPreferencesChanges}
                data-testid="profile-email-notifications-toggle"
              />
            </div>
          </div>
        </div>

        {/* Form Fields */}
        <FormItem>
          <FormControl>
            <TextField
              type="email"
              floatingPlaceholder
              value={me?.email ?? ""}
              placeholder={t("email")}
              disabled
              readOnly
              data-testid="profile-email-input"
            />
          </FormControl>
        </FormItem>

        <FormField
          control={form.control}
          name="firstName"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormControl>
                <TextField
                  {...field}
                  value={field.value || ""}
                  floatingPlaceholder
                  placeholder={t("first_name")}
                  error={fieldState.error}
                  onBlur={handleAutosave}
                  data-testid="profile-firstname-input"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lastName"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormControl>
                <TextField
                  {...field}
                  value={field.value || ""}
                  floatingPlaceholder
                  placeholder={t("last_name")}
                  error={fieldState.error}
                  onBlur={handleAutosave}
                  data-testid="profile-lastname-input"
                />
              </FormControl>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="preferredLanguage"
          render={({ field, fieldState }) => (
            <FormItem>
              <Select
                onValueChange={(value) => {
                  if (!value) return;

                  field.onChange(value);
                  handleAutosave();
                }}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger
                    placeholder={t("language")}
                    floatingPlaceholder
                    helperText={
                      fieldState.error?.message
                        ? t(fieldState.error.message)
                        : undefined
                    }
                    data-testid="profile-language-dropdown-trigger"
                    error={fieldState.error}
                  >
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>

                <SelectContent data-testid="profile-language-dropdown-content">
                  {appLanguageOptions.map((option) => {
                    return (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        data-testid={`profile-language-option-${option.value}`}
                      >
                        {option.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
};
